import { FetchError } from 'ofetch'

export function isAbortError(error: unknown) {
  if (!(error instanceof Error)) return false

  if (typeof error.cause === 'object' && error.cause?.toString().includes('AbortError')) {
    // Wrapped FetchError thrown by ofetch
    return true
  }

  // Native AbortError
  return error.name === 'AbortError'
}

export function isFetchErrorWithStatus(error: unknown, status: number): error is FetchError {
  return error instanceof FetchError && error.response?.status === status
}

/**
 * Flags fetch responses whose error the HTTP layer (`app/plugins/http.ts`) has already handled
 * terminally — e.g. redirected to `/sorry` or triggered a re-login. A caller that wraps a fetch in
 * its own try/catch can consult `isErrorHandled(error)` to avoid handling the same failure twice
 *
 * The flag is a symbol set directly on the response and is visible as `Symbol(errorHandled): true`
 * when inspecting the response while debugging.
 */
const errorHandled = Symbol('errorHandled')

export function markErrorHandled(response: object | null | undefined): void {
  if (response) (response as Record<symbol, unknown>)[errorHandled] = true
}

/**
 * Returns true if the error has been marked as handled by `markErrorHandled()`.
 *
 * This is useful for avoiding double-handling of errors that have already been
 * handled by the HTTP layer.
 *
 * @param error The error to check.
 * @returns True if the error has been marked as handled, false otherwise.
 */
export function isErrorHandled(error: unknown): boolean {
  const response = (error as { response?: Record<symbol, unknown> } | null)?.response
  return Boolean(response?.[errorHandled])
}

/**
 * Reads the request id from a response's `X-Request-Id` header.
 *
 * The BFF sets it on every response and the API gateway echoes the ID on legacy responses, so it
 * is available uniformly.
 *
 * @param response The response object.
 * @returns The request ID, or `undefined` if the header is not present.
 */
export function getRequestId(response: { headers?: Headers } | null | undefined): string | undefined {
  return response?.headers?.get('x-request-id') ?? undefined
}

export function isAccountSuspendedError(error: unknown): boolean {
  return isFetchErrorWithStatus(error, 423)
}
